import {
  type ComponentProps,
  type RefObject,
  useEffect,
  useRef,
  useState,
} from "react";
import type {
  AcceptedDictation,
  DictationIssue,
  DictationTarget,
} from "shared";
import type { DictationView } from "../components/DictationView";
import {
  playDictationEndSound,
  playDictationStartSound,
} from "../utils/dictationSounds";
import {
  EDITOR_PUSH_TO_TALK_HOLD_DELAY_MS,
  useDictationPushToTalk,
} from "./useDictationPushToTalk";
import { usePushToTalkScope } from "./usePushToTalkScope";
import { useVoiceDictation } from "./useVoiceDictation";

interface UseEditorDictationOptions {
  /**
   * The editor that takes ✓'s words. Called when they are inserted, so it
   * always reaches the live editor; null if it's gone.
   */
  getTarget: () => DictationTarget | null;
  /** Read-only or saving: no mic and no push-to-talk. */
  disabled?: boolean;
  /** Words that finish after the editor unmounted — see `useVoiceDictation`. */
  onLeave?: (accepted: AcceptedDictation) => void;
}

export interface EditorDictation {
  /** Show the mic: voice input is on, supported here, and the editor is editable. */
  canDictate: boolean;
  /** The dictation box is up in place of the editor. */
  isDictating: boolean;
  /** Why it couldn't start or ended on its own. The caller shows it, then dismisses it. */
  issue: DictationIssue | null;
  dismissIssue: () => void;
  /** The mic's `onClick`. */
  start: () => void;
  /** Spread into `DictationView`. */
  viewProps: ComponentProps<typeof DictationView>;
  /** The push-to-talk scope: both go on the element wrapping the editor. */
  scopeRef: RefObject<HTMLDivElement>;
  scopeProps: ReturnType<typeof usePushToTalkScope>["scopeProps"];
}

/**
 * Voice dictation for one rich-text editor: the engine, its push-to-talk
 * scope on the screen, and inserting ✓'s words at the caret. Renders nothing
 * — `WithDictation` turns the result into an editor's slots; composers with
 * their own layout (pinned chat) use it directly.
 * Design: .claude/projects/shared/voice-dictation-editors/DESIGN.md §3.4.
 */
export const useEditorDictation = ({
  getTarget,
  disabled = false,
  onLeave,
}: UseEditorDictationOptions): EditorDictation => {
  const dictation = useVoiceDictation({ onLeave });
  const { status, canAccept, issue, dismissIssue } = dictation;
  const isDictating = status !== "idle";
  const canDictate = dictation.isAvailable && !disabled;
  const scopeRef = useRef<HTMLDivElement>(null);
  /**
   * ✓'s words wait here until the dictation box is gone: inserting focuses
   * the editor, and a hidden one can't take focus (Quill then loses the caret).
   */
  const [pendingText, setPendingText] = useState<string | null>(null);

  const start = () => {
    playDictationStartSound();
    void dictation.start();
  };

  const accept = async () => {
    playDictationEndSound();
    const accepted = await dictation.accept();
    if (accepted) setPendingText(accepted.text);
  };

  const discard = () => {
    playDictationEndSound();
    dictation.discard();
  };

  const { isAllowedTarget, scopeProps } = usePushToTalkScope({
    ref: scopeRef,
    enabled: canDictate,
  });
  useDictationPushToTalk({
    enabled: canDictate,
    status,
    canAccept,
    holdDelayMs: EDITOR_PUSH_TO_TALK_HOLD_DELAY_MS,
    isAllowedTarget,
    onStart: start,
    onAccept: () => void accept(),
    onDiscard: discard,
  });

  useEffect(() => {
    if (isDictating || pendingText === null) return;
    setPendingText(null);
    getTarget()?.insert(pendingText);
  }, [isDictating, pendingText, getTarget]);

  return {
    canDictate,
    isDictating,
    issue,
    dismissIssue,
    start,
    viewProps: {
      status,
      segments: dictation.segments,
      tail: dictation.tail,
      canAccept,
      issue,
      elapsedSeconds: dictation.elapsedSeconds,
      maxSeconds: dictation.maxSeconds,
      silenceStopIn: dictation.silenceStopIn,
      levelsRef: dictation.levelsRef,
      onAccept: () => void accept(),
      onDiscard: discard,
    },
    scopeRef,
    scopeProps,
  };
};
